import React from 'react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { 
  Globe, 
  Palette, 
  Code2, 
  Smartphone, 
  Rocket,
  ArrowRight,
  ExternalLink,
  Monitor
} from 'lucide-react';

const WebsiteBuilderShowcase = () => {
  const { language } = useLanguage();
  const t = translations[language]?.websiteBuilder || translations.en.websiteBuilder;

  // Sample websites we've built
  const websites = [
    {
      name: 'Luxe Interiors',
      type: t.projects.types.interior,
      image: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=600&h=400&fit=crop',
      color: '#c9a87c'
    },
    {
      name: 'TechVenture Labs',
      type: t.projects.types.startup,
      image: 'https://images.unsplash.com/photo-1497215842964-222b430dc094?w=600&h=400&fit=crop',
      color: '#6366f1'
    },
    {
      name: 'Bloom & Co',
      type: t.projects.types.ecommerce,
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop',
      color: '#ec4899'
    },
    {
      name: 'Atlas Fitness',
      type: t.projects.types.fitness,
      image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&h=400&fit=crop',
      color: '#f97316'
    }
  ];

  const capabilities = [
    { icon: Palette, text: t.capabilities.design },
    { icon: Code2, text: t.capabilities.development },
    { icon: Smartphone, text: t.capabilities.responsive },
    { icon: Rocket, text: t.capabilities.performance }
  ];

  return (
    <section className="bg-[#302f2c] py-24 md:py-32 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#7dd3fc]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#1e3a5f]/20 rounded-full blur-3xl" />
      </div>

      <div className="max-w-[87.5rem] mx-auto px-10 relative z-10">
        {/* Section Header */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#1e3a5f] text-[#7dd3fc] px-4 py-2 rounded-full text-sm font-semibold uppercase tracking-wider mb-6">
              <Globe size={16} />
              {t.badge}
            </div>
            <h2 className="font-black text-[#7dd3fc] uppercase leading-[0.85] mb-6" style={{ fontSize: 'clamp(2.5rem, 4vw + 1rem, 5rem)' }}>
              {t.headline}
            </h2>
            <p className="text-[#7dd3fc]/70 text-xl mb-8">
              {t.description}
            </p>

            {/* Capabilities */}
            <div className="grid grid-cols-2 gap-4">
              {capabilities.map((cap) => {
                const IconComponent = cap.icon;
                return (
                  <div key={cap.text} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#1a1c1b] rounded-lg flex items-center justify-center">
                      <IconComponent className="w-5 h-5 text-[#7dd3fc]" />
                    </div>
                    <span className="text-[#7dd3fc]/80 text-sm font-medium">{cap.text}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-[#1a1c1b] border border-[#1e3a5f]/50 p-8 text-center">
              <div className="text-[#7dd3fc] font-black text-5xl mb-2">50+</div>
              <div className="text-[#888680] text-sm uppercase tracking-wider">{t.stats.websites}</div>
            </div>
            <div className="bg-[#1a1c1b] border border-[#1e3a5f]/50 p-8 text-center">
              <div className="text-[#7dd3fc] font-black text-5xl mb-2">100%</div>
              <div className="text-[#888680] text-sm uppercase tracking-wider">{t.stats.responsive}</div>
            </div>
            <div className="bg-[#1a1c1b] border border-[#1e3a5f]/50 p-8 text-center">
              <div className="text-[#7dd3fc] font-black text-5xl mb-2">98%</div>
              <div className="text-[#888680] text-sm uppercase tracking-wider">{t.stats.satisfaction}</div>
            </div>
            <div className="bg-[#1a1c1b] border border-[#1e3a5f]/50 p-8 text-center">
              <div className="text-[#7dd3fc] font-black text-5xl mb-2">24h</div>
              <div className="text-[#888680] text-sm uppercase tracking-wider">{t.stats.support}</div>
            </div>
          </div>
        </div>

        {/* Projects Showcase */}
        <div className="mb-12">
          <h3 className="text-[#888680] text-sm font-semibold uppercase tracking-widest mb-6">
            {t.projects.label}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {websites.map((site) => (
              <div 
                key={site.name}
                className="group relative bg-[#1a1c1b] border border-[#1e3a5f]/50 rounded-xl overflow-hidden hover:border-[#7dd3fc]/30 transition-all"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={site.image} 
                    alt={site.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a1c1b] via-transparent to-transparent" />
                  
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-[#1a1c1b]/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-[#7dd3fc] flex items-center justify-center">
                      <Monitor className="w-6 h-6 text-[#1a1c1b]" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <div 
                    className="w-3 h-3 rounded-full mb-3"
                    style={{ backgroundColor: site.color }}
                  />
                  <h4 className="text-[#7dd3fc] font-bold text-lg mb-1">{site.name}</h4>
                  <p className="text-[#888680] text-sm">{site.type}</p>
                </div>

                {/* Bottom accent */}
                <div 
                  className="absolute bottom-0 left-0 right-0 h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"
                  style={{ backgroundColor: site.color }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <p className="text-[#888680] text-lg mb-6">{t.ctaText}</p>
          <Button
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#7dd3fc] text-[#1a1c1b] hover:bg-[#7dd3fc]/90 rounded-full px-10 py-7 font-semibold uppercase tracking-wide text-lg transition-transform hover:scale-[1.02] active:scale-[0.98] inline-flex items-center gap-3"
          >
            {t.cta}
            <ArrowRight size={22} />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default WebsiteBuilderShowcase;
